import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { Loader2, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirmPassword) return toast.error('Passwords do not match');
    if (!token) return toast.error('Invalid reset link');
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-display font-semibold mb-2">Invalid Reset Link</h2>
            <p className="text-sm text-muted-foreground mb-6">This password reset link is invalid or has expired.</p>
            <Button onClick={() => window.location.href = '/login'} data-testid="go-to-login">Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="reset-success-screen">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-display font-semibold mb-2">Password Reset!</h2>
            <p className="text-sm text-muted-foreground mb-6">Your password has been changed successfully. You can now log in with your new password.</p>
            <Button onClick={() => window.location.href = '/login'} data-testid="login-after-reset">Log In Now</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
        <CardHeader className="text-center pb-2">
          <img src="/stp-logo.png"
            alt="Stellar Productions" className="h-14 w-14 object-contain mx-auto mb-2" />
          <CardTitle className="text-xl font-display">Set New Password</CardTitle>
          <p className="text-sm text-muted-foreground">Enter your new password below.</p>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                <PasswordInput id="password" placeholder="Min 6 characters" value={password}
                  onChange={e => setPassword(e.target.value)} className="pl-9" data-testid="new-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                <PasswordInput id="confirm" placeholder="Re-enter password" value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)} className="pl-9" data-testid="confirm-password-input" />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
